import React from "react";
import { Alert, AlertTitle, Box, Button, Container, Paper, Step, StepButton, Stepper, Typography } from "@mui/material";
import { CustomerData, ProjectDescriptionData, ProjectKeyData, ProjectTenderDto } from "../../types/projectTypes";
import KeyDataStep from "../../components/projects/KeyDataStep";
import CustomerStep from "../../components/projects/CustomerStep";
import ProjectDescriptionStep from "../../components/projects/ProjectDescriptionStep";
import SummaryDetails from "../../components/projects/SummaryDetails";
import dayjs from "dayjs";
import { showErrorMessage } from "../../utils/toastUtils";
import {
  validateCustomerData,
  validateProjectDescriptionData,
  validateProjectKeyDataTendering,
} from "../../utils/projectTenderingValidationUtils";
import useProjects from "../../hooks/projects/useProjects";
import { useNavigate, useSearchParams } from "react-router-dom";
import useProjectLeads from "../../hooks/projectLeads/useProjectLeads";
import { ProjectLeadDto } from "../../types/projectLeadTypes";

// Steps for the project tendering form
const steps = ["Rahmendaten", "Kundendaten", "Projektbeschreibung"];

/**
 * Component to create a new project tendering.
 *
 * When launched with a `?fromLead=<id>` query param, the form:
 *   - prefetches the Lead via {@link useProjectLeads},
 *   - prefills projectName and situation from the Lead,
 *   - shows the Lead's free-text hints (Zeitraum, Umsatz, Kunde) as an info
 *     banner above the Kick-Off / Projektstart / Projektdauer fields,
 *   - on submit calls the Lead conversion endpoint to create the initial
 *     project data and link it to the Lead.
 *
 * @returns - A form to create a new project tendering
 */
const ProjectTendering = () => {
  const { allCoreCompetencies } = useProjects();
  const { fetchProjectLead, convertProjectLead } = useProjectLeads();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromLeadParam = searchParams.get("fromLead");
  const parsedFromLeadId = fromLeadParam ? Number(fromLeadParam) : null;
  const fromLeadId = parsedFromLeadId !== null && Number.isFinite(parsedFromLeadId) ? parsedFromLeadId : null;
  const [sourceProjectLead, setSourceProjectLead] = React.useState<ProjectLeadDto | null>(null);

  // Project key data initial state
  const [projectKeyData, setProjectKeyData] = React.useState<ProjectKeyData>({
    projectName: "",
    jobSite: "",
    tenderDate: dayjs(), // will be shown but is disabled
    estimatedProjectStart: undefined,
    estimatedProjectDuration: "",
    estimatedProjectEuroPerBT: undefined,
    estimatedProjectEuroPerBTrange: undefined,
    estimatedProjectBTmin: undefined,
    estimatedProjectBTmax: undefined,
    estimatedProjectMemberMin: undefined,
    estimatedProjectMemberMax: undefined,
    applicationEnd1: undefined,
  });

  // Errors for the project key data inital state
  const [projectKeyDataErrors, setProjectKeyDataErrors] = React.useState<{ [key: string]: boolean }>({
    projectName: false,
    estimatedProjectStart: false,
    estimatedProjectDuration: false,
    estimatedProjectEuroPerBT: false,
    estimatedProjectBTmin: false,
    estimatedProjectBTmax: false,
    estimatedProjectMemberMin: false,
    estimatedProjectMemberMax: false,
    applicationEnd1: false,
  });

  // Customer data initial state
  const [customerData, setCustomerData] = React.useState<CustomerData>({
    companyId: -1,
    name: "",
    shortDescription: "",
    newCustomer: undefined,
    acquisitor: undefined,
    acquisitionMethod: undefined,
    industry: undefined,
    contactPerson: undefined,
    street: "",
    postalCode: "",
    city: "",
    addressAdditional: "",
    url: "",
    importantInformation: "",
    contactDesired: false,
    classified: false,
  });

  // Errors for the customer data initial state
  const [customerDataErrors, setCustomerDataErrors] = React.useState<{ [key: string]: boolean }>({
    customer: false,
    industry: false,
    newCustomer: false,
    contactPerson: false,
    acquisitor: false,
  });

  // Project Description data initial state
  const [projectDescriptionData, setProjectDescriptionData] = React.useState<ProjectDescriptionData>({
    situation: "",
    peculiarities: "",
    coreCompetencies: [],
    requirementProfile: "",
    referenceProjects: "",
    notes: "",
  });

  // Errors for the project description data initial state
  const [projectDescriptionDataErrors, setProjectDescriptionDataErrors] = React.useState<{ [key: string]: boolean }>({
    coreCompetencies: false,
  });

  // states for the active step and the completed steps
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});

  // Prefill from Lead when launched with ?fromLead=<id>.
  // Only fields that are identical between Lead and the later project phases
  // (projectName, situation) are copied over — the Lead's rough free-text
  // hints (Zeitraum / Umsatz / Kunde) are shown separately in an info banner
  // so the HoCR can fill the precise Kick-Off / Projektstart / Projektdauer
  // fields accordingly.
  React.useEffect(() => {
    if (fromLeadId == null) return;
    let cancelled = false;
    fetchProjectLead(fromLeadId)
      .then((projectLead) => {
        if (cancelled) return;
        setSourceProjectLead(projectLead);
        setProjectKeyData((prev) => ({
          ...prev,
          projectName: prev.projectName || projectLead.projectName,
        }));
        setProjectDescriptionData((prev) => ({
          ...prev,
          situation: prev.situation || projectLead.situation || "",
        }));
      })
      .catch(() => {
        showErrorMessage("Lead konnte nicht geladen werden");
      });
    return () => {
      cancelled = true;
    };
  }, [fetchProjectLead, fromLeadId]);

  // Get the total number of steps
  const totalSteps = () => {
    return steps.length;
  };

  // Get the number of completed steps
  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  // Check if the current step is the last step
  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  // Check if all steps are completed
  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  // Save the project through Lead conversion. This creates the project and
  // flips the Lead to "Ausgeschrieben" in one transaction.
  const handleSaveProject = async () => {
    if (fromLeadId == null) {
      showErrorMessage("Projektausschreibung muss aus einem Lead gestartet werden");
      return;
    }

    // All data should be set at this point, but check again to be sure
    if (
      !projectKeyData.projectName ||
      !projectKeyData.estimatedProjectStart ||
      !projectKeyData.estimatedProjectDuration ||
      !projectKeyData.estimatedProjectEuroPerBT ||
      !projectKeyData.estimatedProjectBTmin ||
      !projectKeyData.estimatedProjectBTmax ||
      !projectKeyData.estimatedProjectMemberMin ||
      !projectKeyData.estimatedProjectMemberMax ||
      !projectKeyData.applicationEnd1 ||
      !customerData.acquisitor ||
      !customerData.acquisitionMethod ||
      !customerData.industry ||
      customerData.companyId === -1 ||
      !projectDescriptionData.coreCompetencies ||
      projectDescriptionData.coreCompetencies.length === 0 ||
      !projectKeyData.estimatedProjectEuroPerBT ||
      !customerData.contactPerson ||
      customerData.newCustomer === undefined
    ) {
      // Show an error message if some data is missing
      showErrorMessage("Einige Daten fehlen oder sind fehlerhaft");
      return;
    }
    const data: ProjectTenderDto = {
      projectName: projectKeyData.projectName,
      status: "Lead",
      jobSite: projectKeyData.jobSite,
      tenderDate: projectKeyData.tenderDate?.toDate(),
      estimatedProjectStart: projectKeyData.estimatedProjectStart.toDate(),
      estimatedProjectDuration: projectKeyData.estimatedProjectDuration,
      estimatedProjectEuroPerBT: projectKeyData.estimatedProjectEuroPerBT,
      estimatedProjectEuroPerBTrange: projectKeyData.estimatedProjectEuroPerBTrange,
      estimatedProjectBTmin: projectKeyData.estimatedProjectBTmin,
      estimatedProjectBTmax: projectKeyData.estimatedProjectBTmax,
      estimatedProjectMemberMin: projectKeyData.estimatedProjectMemberMin,
      estimatedProjectMemberMax: projectKeyData.estimatedProjectMemberMax,
      applicationStart1: null,
      applicationEnd1: projectKeyData.applicationEnd1.toDate(),
      applicationStart2: null,
      applicationEnd2: null,
      situation: projectDescriptionData.situation,
      peculiarities: projectDescriptionData.peculiarities,
      coreCompetencies: projectDescriptionData.coreCompetencies,
      requirementProfile: projectDescriptionData.requirementProfile,
      referenceProjects: projectDescriptionData.referenceProjects,
      notes: projectDescriptionData.notes,
      acquisitor: customerData.acquisitor,
      acquisitionMethod: customerData.acquisitionMethod,
      contactPersons: customerData.contactPerson ? [customerData.contactPerson] : [],
      customerType: customerData.newCustomer ? "Neukunde" : "Altkunde",
      kickoff: null,
      staffingCommittee: [],
      client: {
        companyId: customerData.companyId,
        name: customerData.name,
        industry: customerData.industry,
        shortDescription: customerData.shortDescription,
        street: customerData.street,
        postalCode: customerData.postalCode,
        city: customerData.city,
        addressAdditional: customerData.addressAdditional,
        url: customerData.url,
        importantInformation: customerData.importantInformation,
        contactDesired: customerData.contactDesired,
        classified: customerData.classified,
      },
      members: [],
      qms: [],
    };
    try {
      await convertProjectLead(fromLeadId, { project: data });
      navigate("/projekte");
    } catch {
      // toast already handled in useProjectLeads
    }
  };

  // Handle the next step
  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  // Handle the going back a step
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle click on step
  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  // Handle the completion of a step
  const handleComplete = () => {
    // If the active step is the project key data step, validate the project key data and return if there are errors
    if (activeStep === 0) {
      if (validateProjectKeyDataTendering(projectKeyDataErrors, projectKeyData, setProjectKeyDataErrors)) {
        return;
      }
    }
    // If the active step is the customer step, validate the customer data and return if there are errors
    if (activeStep === 1) {
      if (validateCustomerData(customerDataErrors, customerData, setCustomerDataErrors)) {
        return;
      }
    }
    // If the active step is the project description step, validate the project description data and return if there are errors
    if (activeStep === 2) {
      if (
        validateProjectDescriptionData(
          projectDescriptionDataErrors,
          projectDescriptionData,
          setProjectDescriptionDataErrors
        )
      ) {
        return;
      }
    }
    // If there are no errors, set the step as completed and go to the next step
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  // Handle the reset of the steps
  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  // Handle the editing of a step
  const handleEdit = () => {
    const newCompleted = { ...completed };
    delete newCompleted[activeStep];
    setCompleted(newCompleted);
  };

  // Render the current step
  const renderStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <KeyDataStep
            projectKeyData={projectKeyData}
            setProjectKeyData={setProjectKeyData}
            isCompleted={completed[0]}
            errors={projectKeyDataErrors}
            isEditMode={true}
            projectStatus="Lead"
          />
        );
      case 1:
        return (
          <CustomerStep
            customerData={customerData}
            setCustomerData={setCustomerData}
            isCompleted={completed[1]}
            errors={customerDataErrors}
            isEditMode={true}
          />
        );
      case 2:
        return (
          <ProjectDescriptionStep
            projectDescriptionData={projectDescriptionData}
            setProjectDescriptionData={setProjectDescriptionData}
            allCoreCompetencies={allCoreCompetencies}
            isCompleted={completed[2]}
            errors={projectDescriptionDataErrors}
            isEditMode={true}
          />
        );
      default:
        return (
          <KeyDataStep
            projectKeyData={projectKeyData}
            setProjectKeyData={setProjectKeyData}
            isCompleted={completed[0]}
            errors={projectKeyDataErrors}
          />
        );
    }
  };

  if (fromLeadId == null) {
    return (
      <Container maxWidth="md">
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate("/leads")}>
              Zu den Leads
            </Button>
          }
        >
          Projektausschreibungen müssen aus einem Lead gestartet werden.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
        Projektdaten aus Lead erfassen
      </Typography>
      {sourceProjectLead && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Infos aus dem Lead „{sourceProjectLead.projectName}“</AlertTitle>
          <Typography variant="body2">
            <strong>Zeitraum:</strong> {sourceProjectLead.timeframe || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Umsatz:</strong> {sourceProjectLead.revenue || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Kunde:</strong> {sourceProjectLead.customer || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Diese Freitextangaben aus dem Lead dienen als Orientierung — bitte präzise Kick-Off-Datum, Projektstart und
            Projektdauer darunter befüllen.
          </Typography>
        </Alert>
      )}
      <Paper sx={{ paddingX: 4, paddingY: 2 }}>
        <Stepper nonLinear activeStep={activeStep} sx={{ marginLeft: -1 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton color="inherit" onClick={handleStep(index)} sx={{ maxHeight: 2, borderRadius: 5 }}>
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <div>
          {allStepsCompleted() ? (
            <React.Fragment>
              <SummaryDetails
                projectKeyData={projectKeyData}
                customerData={customerData}
                projectDescriptionData={projectDescriptionData}
              />
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Button onClick={handleSaveProject} variant="outlined">
                  Projektdaten speichern
                </Button>
                <Box sx={{ flex: "1 2 auto" }} />
                <Button onClick={handleReset} variant="outlined" color="info">
                  Nochmals Bearbeiten
                </Button>
              </Box>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Box sx={{ marginTop: 3 }}>{renderStep(activeStep)}</Box>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2, ml: -1 }}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                  variant="outlined"
                >
                  Zurück
                </Button>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button onClick={handleNext} sx={{ mr: 1 }} variant="outlined">
                  Weiter
                </Button>
                {activeStep !== steps.length &&
                  (completed[activeStep] ? (
                    <Button onClick={handleEdit} variant="outlined" color="info">
                      Bearbeiten
                    </Button>
                  ) : (
                    <Button onClick={handleComplete} variant="outlined">
                      {completedSteps() === totalSteps() - 1 ? "Abschließen" : "Überprüfen"}
                    </Button>
                  ))}
              </Box>
            </React.Fragment>
          )}
        </div>
      </Paper>
    </Container>
  );
};

export default ProjectTendering;
